import { Map as ImmutableMap, List as ImmutableList, fromJS } from 'immutable';
import http from 'hub-http/clients/apiClient';
import Raven from 'raven-js';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import I18n from 'I18n';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { jsx as _jsx } from "react/jsx-runtime";
const SNIPPET_FOLDERS_URI = 'snippets/v1/folder';
// eslint-disable-next-line no-restricted-syntax
let cache;
export const bustSnippetsApiCache = () => {
  cache = null;
};
const makeSnippetOption = snippet => {
  var _snippet$metadata;
  return {
    text: snippet.name,
    value: snippet.contentId,
    searchText: snippet.shortcut,
    help: /*#__PURE__*/_jsx(TruncateString, {
      children: (_snippet$metadata = snippet.metadata) === null || _snippet$metadata === void 0 ? void 0 : _snippet$metadata.body
    })
  };
};
const createSnippetOptions = (snippetsResponse, folders) => {
  var _snippetsResponse$res;
  const results = (_snippetsResponse$res = snippetsResponse.results) !== null && _snippetsResponse$res !== void 0 ? _snippetsResponse$res : [];
  if (!folders || folders.length === 0) {
    return results.map(makeSnippetOption);
  }
  const folderMap = new Map(folders.map(f => [f.id, f.name]));
  const grouped = {};
  const ungrouped = [];
  for (const snippet of results) {
    if (snippet.folderId != null && folderMap.has(snippet.folderId)) {
      if (!grouped[snippet.folderId]) {
        grouped[snippet.folderId] = [];
      }
      grouped[snippet.folderId].push(snippet);
    } else {
      ungrouped.push(snippet);
    }
  }
  const groups = folders.filter(f => {
    var _grouped$f$id;
    return ((_grouped$f$id = grouped[f.id]) === null || _grouped$f$id === void 0 ? void 0 : _grouped$f$id.length) > 0;
  }).map(f => ({
    text: f.name,
    value: f.id,
    options: grouped[f.id].map(makeSnippetOption)
  }));
  if (ungrouped.length > 0) {
    groups.push(...ungrouped.map(makeSnippetOption));
  }
  return groups;
};
const getSearchBody = ({
  limit = 1000,
  offset = 0,
  query = '',
  sorts = []
} = {}) => ({
  query,
  limit,
  offset,
  sorts,
  filters: [{
    field: 'content_type',
    values: ['SNIPPET']
  }],
  contentTypesToHydrate: ['SNIPPET']
});
function fetchSnippetFolders(httpClient) {
  return httpClient.get(SNIPPET_FOLDERS_URI);
}
export function fetchSnippets(httpClient, searchTerm) {
  let promise;
  if (cache && !searchTerm) {
    promise = Promise.resolve(cache);
    promise.catch(err => {
      setTimeout(() => {
        throw err;
      });
    });
    return promise;
  }
  const foldersPromise = searchTerm ? Promise.resolve([]) : fetchSnippetFolders(httpClient).catch(() => []);
  promise = Promise.all([httpClient.post('salescontentsearch/v2/search', {
    data: getSearchBody({
      query: searchTerm || '',
      limit: searchTerm ? 100 : 1000,
      offset: 0,
      sorts: searchTerm ? [] : [{
        field: 'name',
        order: 'ASC'
      }]
    })
  }), foldersPromise]).then(([res, folders]) => {
    const snippetOptions = createSnippetOptions(res, searchTerm ? undefined : folders);
    if (!searchTerm) {
      cache = snippetOptions;
    }
    return snippetOptions;
  });
  promise.catch(err => {
    setTimeout(() => {
      throw err;
    });
  });
  return promise;
}
export const defaultApiWrapper = apiClient => ({
  fetchRenderedContent({
    id,
    contactEmail,
    contactVid,
    objectType,
    subjectId,
    supplementalObjects
  }) {
    const subjectAsSupplementalObject = objectType && subjectId && subjectId !== 'null' && subjectId !== 'undefined' ? {
      [objectType]: subjectId
    } : {};
    return apiClient.post('snippets/v1/render', {
      data: {
        snippetId: id,
        toEmail: contactEmail,
        contactId: contactVid,
        supplementalObjects: Object.assign({}, supplementalObjects, subjectAsSupplementalObject)
      }
    }).catch(error => {
      var _error$responseJSON;
      if (((_error$responseJSON = error.responseJSON) === null || _error$responseJSON === void 0 ? void 0 : _error$responseJSON.category) === 'UNAUTHORIZED') {
        FloatingAlertStore.addAlert({
          id: 'snippet-error',
          titleText: I18n.text('draftPlugins.snippetsPlugin.permissionsError.title'),
          message: I18n.text('draftPlugins.snippetsPlugin.permissionsError.message'),
          type: 'danger'
        });
      }
      Raven.captureException(error);
    });
  },
  // @ts-expect-error ts-migrate FIXME
  fetchUnrenderedContent({
    id
  }) {
    return apiClient.get(`snippets/v1/snippets/${id}`);
  },
  searchSnippets({
    count,
    offset,
    query
  }) {
    return apiClient.post('salescontentsearch/v2/search', {
      data: getSearchBody({
        limit: count,
        offset,
        query: query || ''
      })
    }).then(response => fromJS(response)).catch(error => {
      Raven.captureException(error);
    });
  },
  searchGroupedSnippets({
    query
  }) {
    return fetchSnippets(apiClient, query);
  }
});
export const fetchAllSnippets = () => http.get('snippets/v1/snippets').then(response => ImmutableMap({
  hasMore: false,
  offset: 0,
  count: response.length,
  total: response.length,
  results: ImmutableList(response).map(snippet => fromJS(snippet))
}));
export const searchSnippets = ({
  count,
  offset,
  query
}) => http.post('salescontentsearch/v2/search', {
  data: getSearchBody({
    limit: count,
    offset,
    query: query || ''
  })
}).then(response => fromJS(response));
export const searchGroupedSnippets = ({
  query
}) => fetchSnippets(http, query);